import { useMutation } from "@apollo/client/react";
import NotificationCard from "@components/notifications/NotificationCard";
import { faBell } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DISMISS_NOTIFICATION } from "@graphql/mutations/DismissNotification";
import { MARK_NOTIFICATION_READ } from "@graphql/mutations/MarkNotificationRead";
import { useVisibleNotifications } from "@hooks";
import { useEffect, useReducer, useState } from "react";
import { useNavigate } from "react-router";

const FILTERS = {
  ALL: "all",
  UNREAD: "unread",
};

export default function CompanionNotifications() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState(FILTERS.ALL);

  const {
    visible,
    loading,
    error,
    markBroadcastRead,
    dismissBroadcast,
  } = useVisibleNotifications({
    domain: "companion",
    prune: true,
  });

  const [, forceUpdate] = useReducer((x) => x + 1, 0);
  useEffect(() => {
    const id = setInterval(forceUpdate, 60_000);
    return () => clearInterval(id);
  }, []);

  const [markRead] = useMutation(MARK_NOTIFICATION_READ, {
    refetchQueries: ["userNotifications"],
  });

  const [dismiss] = useMutation(DISMISS_NOTIFICATION, {
    refetchQueries: ["userNotifications"],
  });

  const handleMarkRead = (n) => {
    if (n.isBroadcast) {
      markBroadcastRead(n.uuid);
      return Promise.resolve();
    }
    return markRead({ variables: { notificationUuid: n.uuid } });
  };

  const handleDismiss = (n) => {
    if (n.isBroadcast) {
      dismissBroadcast(n.uuid);
      return;
    }
    dismiss({ variables: { notificationUuid: n.uuid } });
  };

  const handleRowClick = (n) => {
    if (!n.readAt) {
      handleMarkRead(n).catch(() => {});
    }
    if (n.actionUrl) {
      if (/^https?:\/\//i.test(n.actionUrl)) {
        window.open(n.actionUrl, "_blank", "noopener,noreferrer");
      } else {
        navigate(n.actionUrl);
      }
      return;
    }
    if (n.noteUuid) {
      navigate(`/edit_note/${n.noteUuid}`);
    }
  };

  const sorted = visible
    .slice()
    .sort(
      (a, b) =>
        new Date(b.activeAt ?? b.createdAt).getTime() -
        new Date(a.activeAt ?? a.createdAt).getTime(),
    );

  const unreadCount = sorted.filter((n) => !n.readAt).length;
  const shown =
    filter === FILTERS.UNREAD
      ? sorted.filter((n) => !n.readAt)
      : sorted;

  const pillClass = (active) =>
    `px-3 py-1 rounded-full text-sm border ${
      active
        ? "bg-white text-indigo-700 border-white"
        : "bg-transparent text-white border-white/40 hover:bg-white/10"
    }`;

  return (
    <div className="flex flex-col gap-3 p-2">
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setFilter(FILTERS.ALL)}
          className={pillClass(filter === FILTERS.ALL)}
          data-testid="companion-notifications-filter-all"
        >
          All
        </button>
        <button
          type="button"
          onClick={() => setFilter(FILTERS.UNREAD)}
          className={pillClass(filter === FILTERS.UNREAD)}
          data-testid="companion-notifications-filter-unread"
        >
          Unread ({unreadCount})
        </button>
      </div>

      {loading && sorted.length === 0 ? (
        <div className="text-white/70 py-8 text-center">Loading…</div>
      ) : error ? (
        <div className="text-red-300 py-8 text-center">
          Failed to load notifications.
        </div>
      ) : shown.length === 0 ? (
        <div className="flex flex-col items-center text-white/70 py-16">
          <FontAwesomeIcon icon={faBell} size="2x" className="mb-3" />
          <div>
            {filter === FILTERS.UNREAD
              ? "No unread notifications"
              : "No notifications yet"}
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {shown.map((n) => (
            <NotificationCard
              key={n.uuid}
              notification={n}
              onClick={handleRowClick}
              onMarkRead={handleMarkRead}
              onDismiss={handleDismiss}
            />
          ))}
        </div>
      )}
    </div>
  );
}
